import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Spinner } from 'reactstrap';
import { shipmentHost } from '../../urls/api-shipments/merchant';
import axios from 'axios';
import fedex_logo_purple from '../../assets/img/Fedex-logo-purple.png';

const FedexTrackingModal = ({ isOpen, toggle, shipmentId }) => {
    const [trackingData, setTrackingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && shipmentId) {
            fetchTracking();
        }
    }, [isOpen, shipmentId]);

    const fetchTracking = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${shipmentHost}/shipments/${shipmentId}/track`, {
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('access_token')
                }
            });

            if (response.data.status === 'successful') {
                setTrackingData(response.data);
            } else {
                throw new Error('Failed to fetch tracking data');
            }
        } catch (err) {
            setError('Failed to fetch tracking information. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const trackResult =
        trackingData &&
        trackingData.payload &&
        trackingData.payload.output &&
        trackingData.payload.output.completeTrackResults &&
        trackingData.payload.output.completeTrackResults[0] &&
        trackingData.payload.output.completeTrackResults[0].trackResults &&
        trackingData.payload.output.completeTrackResults[0].trackResults[0];

    const trackingNumber = trackResult && trackResult.trackingNumberInfo && trackResult.trackingNumberInfo.trackingNumber;

    const status = trackResult && trackResult.latestStatusDetail && trackResult.latestStatusDetail.description;

    const deliveryTime =
        trackResult &&
        trackResult.dateAndTimes &&
        trackResult.dateAndTimes.find(dt => dt.type === 'ACTUAL_DELIVERY') &&
        trackResult.dateAndTimes.find(dt => dt.type === 'ACTUAL_DELIVERY').dateTime;

    const deliveryLocation = trackResult && trackResult.latestStatusDetail && trackResult.latestStatusDetail.scanLocation && trackResult.latestStatusDetail.scanLocation.city;

    const deliveryNote =
        trackResult &&
        trackResult.latestStatusDetail &&
        trackResult.latestStatusDetail.ancillaryDetails &&
        trackResult.latestStatusDetail.ancillaryDetails[0] &&
        trackResult.latestStatusDetail.ancillaryDetails[0].reasonDescription;

    const shipperCity = trackResult && trackResult.shipperInformation && trackResult.shipperInformation.address && trackResult.shipperInformation.address.city;

    const recipientCity = trackResult && trackResult.recipientInformation && trackResult.recipientInformation.address && trackResult.recipientInformation.address.city;

    const weight =
        trackResult &&
        trackResult.packageDetails &&
        trackResult.packageDetails.weightAndDimensions &&
        trackResult.packageDetails.weightAndDimensions.weight &&
        trackResult.packageDetails.weightAndDimensions.weight[0];

    const dimensions =
        trackResult &&
        trackResult.packageDetails &&
        trackResult.packageDetails.weightAndDimensions &&
        trackResult.packageDetails.weightAndDimensions.dimensions &&
        trackResult.packageDetails.weightAndDimensions.dimensions[0];

    const packaging = trackResult && trackResult.packageDetails && trackResult.packageDetails.packagingDescription && trackResult.packageDetails.packagingDescription.description;

    const scanEvent = trackResult && trackResult.scanEvents && trackResult.scanEvents[0];

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
            <ModalHeader toggle={toggle}>
                <div className="d-flex align-items-center">
                    <img
                        src={fedex_logo_purple}
                        alt="FedEx"
                        style={{
                            width: '50px',
                            height: '50px',
                            marginLeft: '10px',
                            marginRight: '5px',
                            objectFit: 'contain'
                        }}
                    />
                    <span style={{ color: '#333' }}>Tracking Details – #{trackingNumber}</span>
                </div>
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center py-4">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading tracking information...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error}
                        <Button color="link" className="p-0 ms-2" onClick={fetchTracking}>
                            Try Again
                        </Button>
                    </div>
                ) : trackingData ? (
                    <div>
                        <h4 style={{ color: '#333' }}>
                            Status: <span className="text-success">{status}</span>
                        </h4>
                        {deliveryTime && (
                            <p>
                                <strong>Delivered At:</strong> {new Date(deliveryTime).toLocaleString()}
                            </p>
                        )}
                        {deliveryLocation && (
                            <p>
                                <strong>Delivery Location:</strong> {deliveryLocation}
                            </p>
                        )}
                        {deliveryNote && (
                            <p>
                                <strong>Note:</strong> {deliveryNote}
                            </p>
                        )}

                        <hr />

                        <h4 style={{ color: '#333' }}>Package Info</h4>
                        {weight && (
                            <p>
                                <strong>Weight:</strong> {weight.value} {weight.unit}
                            </p>
                        )}
                        {dimensions && (
                            <p>
                                <strong>Dimensions:</strong> {dimensions.length}x{dimensions.width}x{dimensions.height} {dimensions.units}
                            </p>
                        )}
                        {packaging && (
                            <p>
                                <strong>Packaging:</strong> {packaging}
                            </p>
                        )}

                        <hr />

                        <h4 style={{ color: '#333' }}>Route</h4>
                        {shipperCity && (
                            <p>
                                <strong>Shipped From:</strong> {shipperCity}
                            </p>
                        )}
                        {recipientCity && (
                            <p>
                                <strong>Delivered To:</strong> {recipientCity}
                            </p>
                        )}

                        <hr />

                        <h4 style={{ color: '#333' }}>Latest Scan</h4>
                        {scanEvent && (
                            <p>
                                {scanEvent.eventDescription} – {scanEvent.scanLocation && scanEvent.scanLocation.city},{' '}
                                {scanEvent.scanLocation && scanEvent.scanLocation.stateOrProvinceCode} on {new Date(scanEvent.date).toLocaleString()}
                            </p>
                        )}
                    </div>
                ) : null}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" onClick={toggle}>
                    Close
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default FedexTrackingModal;
